import {ID_PREFIX} from "../utils/constants";
import {debug, warn} from "../utils/log";
import {createContext} from "./context-shared";
import {get} from "../utils/storage";

const context = createContext({
    state: 'initializing',
    solution: '',
    rate: 0,
    errorMsg: '',
    errorId: ''
})
const stateChannel = new BroadcastChannel(`${ID_PREFIX}state`);
stateChannel.addEventListener('message', async (event) => {
    if (event.data.type === 'state-dump' && event.data.state && typeof event.data.state === 'object') {
        context.updateState(event.data.state);
    }
})
export const data = context.view
export const onChanges = context.onChanges

const waitForInitialState = new Promise<void>((resolve) => {
    onChanges('*', () => resolve())
})

const implementation = (async () => {
    for (const impl of [{
        id: 'shared-worker',
        module: import('./real/shared'),
    },{
        id: 'service-worker',
        module: import('./real/sw'),
    }, {
        id: 'inline',
        module: import('./real/inline'),
    }] as const) {
        const module = await impl.module
        try {
            await module.setup(context)
        } catch (err) {
            warn(`Failed to setup implementation ${impl.id}:`, err);
            continue
        }
        debug(`Implementation ${impl.id} initialized successfully.`);
        return impl
    }
    throw new Error("No implementation found")
})()

/** Promise that resolves when the initial state is set, or we get an error while trying to reach the leader */
export const waitForSetup = (async () => {
    const initial = await get('state')
    if (initial && typeof initial === 'object') {
        try {
            context.updateState(initial);
        } catch (err) {
            warn("Failed to parse initial state from storage, ignoring.", err);
        }
    } else {
        warn("No initial state found in storage, waiting for the leader to provide it.");
    }
    const impl = await implementation
    debug(`Context initialized with implementation: ${impl.id}`)
    setTimeout(() => _requestValidation(false), 10)
    await waitForInitialState
})()
let nextValidation: number | undefined;

/**
 * Sends a validation request to the leader node. The leader decides whether to start the validation process based on the current state. When the validation fails, the leader will get a new solution.
 *
 * @return {Promise<void>} A promise that resolves when the request is successfully sent. The leader will handle the validation process asynchronously. You can listen to the state changes to get the result.
 */
export async function requestValidation(): Promise<void> {
    return await _requestValidation(true)
}

async function _requestValidation(once: boolean): Promise<void> {
    const impl = await implementation
    debug("requestValidation", impl.id)
    await (await impl.module).requestValidation()
    if (once) return;
    // Schedule the next validation request in 1 hour
    clearTimeout(nextValidation);
    nextValidation = window.setTimeout(() => _requestValidation(false), 1000 * 60 * 60);
}

// stop periodic validation requests when the page is hidden.
addEventListener('pagehide', async () => {
    clearTimeout(nextValidation);
    (await (await implementation).module).onpagehide()
})

// restart periodic validation and maybe reload the Service Worker iframe
addEventListener('pageshow', async (event) => {
    if ((event as unknown as { persisted: boolean }).persisted) {
        (await (await implementation).module).bfCacheRestore()
        await _requestValidation(false)
    }
})
